import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Thermometer, Snowflake, Flame, ChefHat, Timer, AlertTriangle,
    TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp,
    Play, Square, LayoutDashboard, RefreshCw,
    Settings2, Plus, Pencil, Trash2, Wifi, WifiOff, Save, X,
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

// ── Status config ──────────────────────────────────────────────────────────────

const STATUS = {
    ok:       { color: 'text-green-500',  bg: 'bg-green-500/10',  border: 'border-green-500/30',  dot: 'bg-green-500'  },
    alarm:    { color: 'text-amber-500',  bg: 'bg-amber-500/10',  border: 'border-amber-500/30',  dot: 'bg-amber-500'  },
    critical: { color: 'text-red-500',    bg: 'bg-red-500/10',    border: 'border-red-500/30',    dot: 'bg-red-500'    },
    offline:  { color: 'text-slate-400',  bg: 'bg-slate-500/10',  border: 'border-slate-500/30',  dot: 'bg-slate-400'  },
    idle:     { color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30',   dot: 'bg-blue-400'   },
};

function statusLabel(status, t) {
    return ({
        ok: t('temp.statusOk'), alarm: t('temp.statusAlarm'),
        critical: t('temp.statusCritical'), offline: t('temp.statusOffline'), idle: t('temp.statusIdle'),
    })[status] ?? status;
}

// ── Source badge ───────────────────────────────────────────────────────────────

function SourceBadge({ sourceType, online }) {
    if (sourceType === 'esp32')
        return online
            ? <span className="text-[10px] font-medium text-green-500">🟢 Capteur réel</span>
            : <span className="text-[10px] font-medium text-red-400">🔴 Hors ligne</span>;
    return <span className="text-[10px] font-medium text-amber-400">🟡 Simulation</span>;
}

// ── Pure sub-components ───────────────────────────────────────────────────────

const StatusBadge = React.memo(function StatusBadge({ status, t }) {
    const cfg = STATUS[status] ?? STATUS.offline;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {statusLabel(status, t)}
        </span>
    );
});

function TempValue({ value, size = 'lg' }) {
    if (value === null || value === undefined) return <span className="text-muted-foreground">--</span>;
    const cls = size === 'xl' ? 'text-4xl font-bold' : size === 'lg' ? 'text-2xl font-bold' : 'text-lg font-semibold';
    return (
        <span className={cls}>
            {value > 0 ? '+' : ''}{value.toFixed(1)}
            <span className="text-base font-normal text-muted-foreground ml-0.5">°C</span>
        </span>
    );
}

function DeviationBadge({ deviation }) {
    if (deviation === null || deviation === undefined) return null;
    if (Math.abs(deviation) < 0.1) return <Minus className="w-4 h-4 text-green-500 inline" />;
    if (deviation > 0)
        return <span className="text-xs font-medium text-amber-500 flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />+{deviation.toFixed(1)}°</span>;
    return <span className="text-xs font-medium text-blue-400 flex items-center gap-0.5"><TrendingDown className="w-3 h-3" />{deviation.toFixed(1)}°</span>;
}

// ── CookingTimer — client-side 1 s countdown ─────────────────────────────────

function CookingTimer({ startTime, cookTimeMinutes }) {
    const totalMs = cookTimeMinutes * 60 * 1000;
    const compute = () => Math.max(0, totalMs - (Date.now() - new Date(startTime).getTime()));
    const [remaining, setRemaining] = useState(compute);

    useEffect(() => {
        setRemaining(compute());
        const id = setInterval(() => setRemaining(compute()), 1000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime, cookTimeMinutes]);

    const mins   = Math.floor(remaining / 60000);
    const secs   = Math.floor((remaining % 60000) / 1000);
    const isDone = remaining === 0;
    const pct    = Math.min(100, 100 - (remaining / totalMs) * 100);

    return (
        <div className="flex flex-col items-center gap-1">
            <span className={`font-mono text-3xl font-bold tabular-nums ${isDone ? 'text-green-500 animate-pulse' : 'text-foreground'}`}>
                {isDone ? '✓ Terminé' : `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`}
            </span>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-[width] duration-1000 ease-linear ${isDone ? 'bg-green-500' : 'bg-primary'}`}
                    style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

// ── TempChart ──────────────────────────────────────────────────────────────────

const TempChart = React.memo(function TempChart({ logs, targetTemp, maxTemp, deviceType }) {
    if (!logs || logs.length === 0)
        return <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">Chargement…</div>;

    const temps  = logs.map(l => l.temperature);
    const minT   = Math.min(...temps, targetTemp ?? 0);
    const maxT   = Math.max(...temps, targetTemp ?? 0, maxTemp ?? 0);
    const pad    = Math.max(2, (maxT - minT) * 0.15);
    const domain = [Math.floor(minT - pad), Math.ceil(maxT + pad)];
    const color  = deviceType === 'freezer' ? '#60a5fa' : '#f97316';

    return (
        <ResponsiveContainer width="100%" height={140}>
            <LineChart data={logs} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.4} />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} interval="preserveStartEnd" />
                <YAxis domain={domain} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} tickFormatter={v => `${v}°`} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.5rem', fontSize: 12 }}
                    formatter={v => [`${v}°C`, 'Temp']} />
                {targetTemp > 0 && <ReferenceLine y={targetTemp} stroke="#22c55e" strokeDasharray="4 2" strokeWidth={1.5}
                    label={{ value: `${targetTemp}°`, fill: '#22c55e', fontSize: 10, position: 'insideTopRight' }} />}
                {maxTemp && <ReferenceLine y={maxTemp} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={1}
                    label={{ value: `max ${maxTemp}°`, fill: '#ef4444', fontSize: 10, position: 'insideTopRight' }} />}
                <Line type="monotone" dataKey="temperature" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
            </LineChart>
        </ResponsiveContainer>
    );
});

// ── DeviceModal — add / edit an equipment ─────────────────────────────────────

const EMPTY_FORM = {
    name: '', type: 'freezer', location: '',
    target_temp: -18, max_temp: 280,
    alert_threshold_high: 3, alert_threshold_low: 5,
    enabled: true, source_type: 'simulation', source_id: '',
};

function DeviceModal({ device, onClose, onSaved, authHeader, addToast, t }) {
    const isEdit = !!device;
    const [form, setForm] = useState(() => device ? {
        name:                  device.name,
        type:                  device.type,
        location:              device.location ?? '',
        target_temp:           device.target_temp,
        max_temp:              device.max_temp ?? 280,
        alert_threshold_high:  device.alert_threshold_high,
        alert_threshold_low:   device.alert_threshold_low,
        enabled:               device.enabled,
        source_type:           device.source_type ?? 'simulation',
        source_id:             device.source_id ?? '',
    } : { ...EMPTY_FORM });

    const [espTab,       setEspTab]       = useState('seen');
    const [seenList,     setSeenList]     = useState([]);
    const [loadingSeen,  setLoadingSeen]  = useState(false);
    const [manualHost,   setManualHost]   = useState('');
    const [manualPort,   setManualPort]   = useState(80);
    const [testResult,   setTestResult]   = useState(null);
    const [testing,      setTesting]      = useState(false);
    const [saving,       setSaving]       = useState(false);

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    useEffect(() => {
        if (form.source_type !== 'esp32' || espTab !== 'seen') return;
        setLoadingSeen(true);
        fetch('/api/temperature/seen', { headers: authHeader() })
            .then(r => r.ok ? r.json() : [])
            .then(setSeenList)
            .catch(() => setSeenList([]))
            .finally(() => setLoadingSeen(false));
    }, [form.source_type, espTab, authHeader]);

    const testConnection = async () => {
        if (!manualHost.trim()) return;
        setTesting(true);
        setTestResult(null);
        try {
            const res = await fetch('/api/temperature/test-connection', {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ host: manualHost.trim(), port: Number(manualPort) }),
            });
            const data = await res.json();
            setTestResult(data);
            if (data.ok && data.esp32?.sensors) {
                const first = Object.keys(data.esp32.sensors)[0];
                if (first && !form.source_id) set('source_id', first);
            }
        } catch {
            setTestResult({ ok: false, error: 'Erreur réseau' });
        } finally {
            setTesting(false);
        }
    };

    const save = async () => {
        if (!form.name.trim()) { addToast({ type: 'error', message: 'Le nom est requis' }); return; }
        if (form.source_type === 'esp32' && !form.source_id.trim()) {
            addToast({ type: 'error', message: 'L\'identifiant capteur (source_id) est requis' }); return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                target_temp:           Number(form.target_temp),
                max_temp:              form.type === 'oven' ? Number(form.max_temp) : null,
                alert_threshold_high:  Number(form.alert_threshold_high),
                alert_threshold_low:   Number(form.alert_threshold_low),
                source_id:             form.source_type === 'esp32' ? form.source_id.trim() : null,
            };
            const url    = isEdit ? `/api/temperature/devices/${device.id}` : '/api/temperature/devices';
            const method = isEdit ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method,
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (res.ok) {
                addToast({ type: 'success', message: t('temp.deviceSaved') });
                onSaved();
                onClose();
            } else {
                const err = await res.json().catch(() => ({}));
                addToast({ type: 'error', message: err.detail ?? 'Erreur sauvegarde' });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
                    <h2 className="text-base font-bold text-foreground">
                        {isEdit ? t('temp.editDevice') : t('temp.addDevice')}
                    </h2>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">

                    {/* Name + Location */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Nom *</label>
                            <input value={form.name} onChange={e => set('name', e.target.value)}
                                placeholder="Congélateur réserve, Four 1…"
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Emplacement</label>
                            <input value={form.location} onChange={e => set('location', e.target.value)}
                                placeholder="Cuisine, Réserve, Cave…"
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                        </div>
                    </div>

                    {/* Type */}
                    <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1.5">Type d'équipement</label>
                        <div className="flex gap-2">
                            {[['freezer', Snowflake, 'text-blue-400', 'Congélateur / Frigo'], ['oven', Flame, 'text-orange-400', 'Four']].map(([tp, Icon, ic, label]) => (
                                <button key={tp} onClick={() => { set('type', tp); set('target_temp', tp === 'freezer' ? -18 : 0); }}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-all
                                        ${form.type === tp ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                                    <Icon className={`w-4 h-4 ${form.type === tp ? '' : ic}`} />
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Temperature thresholds */}
                    <div className={`grid gap-3 ${form.type === 'oven' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}>
                        <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Consigne (°C)</label>
                            <input type="number" value={form.target_temp} onChange={e => set('target_temp', e.target.value)}
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                        </div>
                        {form.type === 'oven' && (
                            <div>
                                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Max (°C)</label>
                                <input type="number" value={form.max_temp} onChange={e => set('max_temp', e.target.value)}
                                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Seuil haut (°C)</label>
                            <input type="number" value={form.alert_threshold_high} onChange={e => set('alert_threshold_high', e.target.value)}
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Seuil bas (°C)</label>
                            <input type="number" value={form.alert_threshold_low} onChange={e => set('alert_threshold_low', e.target.value)}
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                        </div>
                    </div>

                    {/* Source type */}
                    <div className="border border-border rounded-xl p-4 space-y-4">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className="text-sm font-semibold text-foreground">Source de données</span>
                            <div className="flex gap-1 bg-muted/60 rounded-lg p-1">
                                {[['simulation', '🟡 Simulation'], ['esp32', '🟢 Capteur ESP32']].map(([src, label]) => (
                                    <button key={src} onClick={() => set('source_type', src)}
                                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all
                                            ${form.source_type === src ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {form.source_type === 'simulation' && (
                            <p className="text-xs text-muted-foreground">
                                Les températures seront générées automatiquement. Aucune configuration supplémentaire requise.
                            </p>
                        )}

                        {form.source_type === 'esp32' && (
                            <div className="space-y-4">
                                {/* Sub-tabs */}
                                <div className="flex gap-1 border-b border-border pb-2">
                                    {[['seen', '📡 Découverte auto'], ['manual', '🔧 Saisie manuelle']].map(([id, label]) => (
                                        <button key={id} onClick={() => setEspTab(id)}
                                            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors
                                                ${espTab === id ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {/* Seen ESP32s */}
                                {espTab === 'seen' && (
                                    <div className="space-y-2 min-h-[60px]">
                                        {loadingSeen && <p className="text-xs text-muted-foreground py-2">Chargement…</p>}
                                        {!loadingSeen && seenList.length === 0 && (
                                            <div className="text-center py-4">
                                                <WifiOff className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                                <p className="text-xs text-muted-foreground">Aucun ESP32 détecté.</p>
                                                <p className="text-[11px] text-muted-foreground mt-0.5">Les capteurs apparaissent ici dès leur première connexion.</p>
                                            </div>
                                        )}
                                        {seenList.map(seen => (
                                            <div key={seen.source_id} className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2.5 gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-foreground">{seen.source_id}</p>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {seen.esp32_id ? `ESP32 : ${seen.esp32_id} · ` : ''}{seen.ip} · {new Date(seen.last_seen).toLocaleTimeString()}
                                                    </p>
                                                </div>
                                                <button onClick={() => set('source_id', seen.source_id)}
                                                    className={`shrink-0 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors
                                                        ${form.source_id === seen.source_id
                                                            ? 'bg-primary text-primary-foreground'
                                                            : 'bg-card border border-border text-foreground hover:border-primary/50'}`}>
                                                    {form.source_id === seen.source_id ? '✓ Sélectionné' : 'Utiliser'}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Manual entry + test */}
                                {espTab === 'manual' && (
                                    <div className="space-y-3">
                                        <div className="flex gap-2 items-end">
                                            <div className="flex-1">
                                                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Adresse IP</label>
                                                <input value={manualHost} onChange={e => setManualHost(e.target.value)}
                                                    placeholder="192.168.1.55" onKeyDown={e => e.key === 'Enter' && testConnection()}
                                                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                                            </div>
                                            <div className="w-20">
                                                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Port</label>
                                                <input type="number" value={manualPort} onChange={e => setManualPort(Number(e.target.value))}
                                                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
                                            </div>
                                            <button onClick={testConnection} disabled={testing || !manualHost.trim()}
                                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 text-sm font-medium disabled:opacity-50 transition-colors whitespace-nowrap">
                                                {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
                                                Tester
                                            </button>
                                        </div>

                                        {testResult && (
                                            <div className={`rounded-lg p-3 text-xs ${testResult.ok ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
                                                {testResult.ok ? (
                                                    <>
                                                        <p className="font-semibold mb-1">✓ Appareil joignable</p>
                                                        {testResult.esp32 && (
                                                            <div className="text-green-400 space-y-0.5">
                                                                {testResult.esp32.esp32_id && <p>ID ESP32 : <strong>{testResult.esp32.esp32_id}</strong></p>}
                                                                {testResult.esp32.sensors && (
                                                                    <p>Capteurs : {Object.entries(testResult.esp32.sensors).map(([k, v]) => `${k} (${Number(v).toFixed(1)}°C)`).join(', ')}</p>
                                                                )}
                                                            </div>
                                                        )}
                                                        {testResult.tcp_only && <p className="text-green-400">{testResult.message}</p>}
                                                    </>
                                                ) : (
                                                    <p>✗ {testResult.error ?? 'Inaccessible'}</p>
                                                )}
                                            </div>
                                        )}

                                        {testResult?.ok && testResult?.esp32?.sensors && (
                                            <div>
                                                <p className="text-xs text-muted-foreground mb-1.5">Capteurs disponibles :</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {Object.keys(testResult.esp32.sensors).map(k => (
                                                        <button key={k} onClick={() => set('source_id', k)}
                                                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                                                                ${form.source_id === k ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary/50 text-foreground'}`}>
                                                            {k}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* source_id field — always visible in ESP32 mode */}
                                <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                        Identifiant capteur <code className="bg-muted px-1 rounded">source_id</code> *
                                    </label>
                                    <input value={form.source_id} onChange={e => set('source_id', e.target.value)}
                                        placeholder="ex: four1, freezer_A, congelateur_reserve"
                                        className={`w-full bg-background border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40
                                            ${form.source_id ? 'border-green-500/50' : 'border-border'}`} />
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Doit correspondre à <code className="bg-muted px-1 rounded">device_id</code> (mode single) ou à la clé dans <code className="bg-muted px-1 rounded">sensors</code> (mode multi) du sketch ESP32.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Active toggle */}
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                        <div className={`relative w-10 h-6 rounded-full transition-colors ${form.enabled ? 'bg-primary' : 'bg-muted'}`}
                            onClick={() => set('enabled', !form.enabled)}>
                            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.enabled ? 'left-5' : 'left-1'}`} />
                        </div>
                        <span className="text-sm font-medium text-foreground">Équipement actif</span>
                    </label>
                </div>

                {/* Footer */}
                <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
                    <button onClick={onClose}
                        className="flex-1 px-4 py-2 rounded-lg border border-border text-muted-foreground hover:text-foreground text-sm font-medium transition-colors">
                        Annuler
                    </button>
                    <button onClick={save} disabled={saving}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isEdit ? 'Mettre à jour' : t('temp.addDevice')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── DeviceManagementPanel ─────────────────────────────────────────────────────

function DeviceManagementPanel({ devices, onAdd, onEdit, onDelete, t }) {
    const [confirmId, setConfirmId] = useState(null);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-foreground">{t('temp.deviceList')}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {devices.length} équipement{devices.length !== 1 ? 's' : ''}
                    </p>
                </div>
                <button onClick={onAdd}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                    <Plus className="w-4 h-4" />
                    {t('temp.addDevice')}
                </button>
            </div>

            {devices.length === 0 ? (
                <div className="bg-card border border-dashed border-border rounded-xl p-10 text-center">
                    <Thermometer className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">Aucun équipement configuré</p>
                    <button onClick={onAdd} className="mt-3 text-primary text-sm hover:underline underline-offset-4">
                        Ajouter le premier équipement
                    </button>
                </div>
            ) : (
                <div className="bg-card border border-border rounded-xl divide-y divide-border overflow-hidden">
                    {devices.map(d => {
                        const TypeIcon   = d.type === 'freezer' ? Snowflake : Flame;
                        const iconColor  = d.type === 'freezer' ? 'text-blue-400' : 'text-orange-400';
                        const isConfirm  = confirmId === d.id;
                        return (
                            <div key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                                <div className={`p-2 rounded-lg shrink-0 ${STATUS[d.status]?.bg ?? STATUS.offline.bg}`}>
                                    <TypeIcon className={`w-4 h-4 ${iconColor}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-foreground text-sm">{d.name}</span>
                                        <StatusBadge status={d.status} t={t} />
                                        <SourceBadge sourceType={d.source_type} online={d.online} />
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                        {[d.location, d.source_id && `id: ${d.source_id}`, d.current_temp !== null && `${d.current_temp}°C`].filter(Boolean).join(' · ')}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {isConfirm ? (
                                        <>
                                            <span className="text-xs text-red-400 font-medium hidden sm:inline">Supprimer ?</span>
                                            <button onClick={() => { onDelete(d.id); setConfirmId(null); }}
                                                className="px-2.5 py-1 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs font-medium transition-colors">Oui</button>
                                            <button onClick={() => setConfirmId(null)}
                                                className="px-2.5 py-1 rounded-lg bg-muted text-muted-foreground text-xs font-medium transition-colors">Non</button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => onEdit(d)} title="Modifier"
                                                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => setConfirmId(d.id)} title="Supprimer"
                                                className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── DeviceCard (overview / tabs) ──────────────────────────────────────────────

const DeviceCard = React.memo(function DeviceCard({ device, logs, expanded, onExpand, onLoadLogs, t }) {
    const cfg      = STATUS[device.status] ?? STATUS.offline;
    const TypeIcon = device.type === 'freezer' ? Snowflake : Flame;
    const ic       = device.type === 'freezer' ? 'text-blue-400' : 'text-orange-400';

    return (
        <div className={`bg-card border rounded-xl p-4 transition-all ${cfg.border}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg ${cfg.bg}`}><TypeIcon className={`w-5 h-5 ${ic}`} /></div>
                    <div className="min-w-0">
                        <p className="font-semibold text-foreground truncate">{device.name}</p>
                        {device.location && <p className="text-xs text-muted-foreground">{device.location}</p>}
                        <SourceBadge sourceType={device.source_type} online={device.online} />
                    </div>
                </div>
                <StatusBadge status={device.status} t={t} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div><p className="text-xs text-muted-foreground mb-1">{t('temp.currentTemp')}</p><TempValue value={device.current_temp} size="lg" /></div>
                <div><p className="text-xs text-muted-foreground mb-1">{t('temp.targetTemp')}</p>
                    <span className="text-lg font-semibold text-muted-foreground">{device.target_temp > 0 ? '+' : ''}{device.target_temp}°</span></div>
                <div><p className="text-xs text-muted-foreground mb-1">{t('temp.deviation')}</p>
                    <div className="flex justify-center"><DeviationBadge deviation={device.deviation} /></div></div>
            </div>
            {device.type === 'oven' && device.max_temp && (
                <p className="text-xs text-muted-foreground mt-2 text-center">Max autorisé : {device.max_temp}°C</p>
            )}
            <button onClick={() => { if (!expanded) onLoadLogs(device.id); onExpand(device.id); }}
                className="mt-3 w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1">
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {t('temp.history')}
            </button>
            {expanded && <div className="mt-2 border-t border-border pt-3">
                <TempChart logs={logs} targetTemp={device.target_temp} maxTemp={device.type === 'oven' ? device.max_temp : null} deviceType={device.type} />
            </div>}
        </div>
    );
});

// ── ProductCard ────────────────────────────────────────────────────────────────

const ProductCard = React.memo(function ProductCard({ product, selected, onSelect }) {
    return (
        <button onClick={() => onSelect(product)}
            className={`bg-card border rounded-xl p-5 flex flex-col items-center gap-3 transition-all hover:border-primary/50 hover:shadow-md w-full
                ${selected ? 'border-primary ring-2 ring-primary/20 shadow-md' : 'border-border'}`}>
            <span className="text-5xl">{product.image ?? '🍽️'}</span>
            <div className="text-center">
                <p className="font-semibold text-foreground">{product.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{product.target_temp}°C · {product.cook_time_minutes} min</p>
            </div>
        </button>
    );
});

// ── ActiveSessionCard ──────────────────────────────────────────────────────────

const ActiveSessionCard = React.memo(function ActiveSessionCard({ session, onComplete, onCancel, t }) {
    return (
        <div className="bg-card border border-primary/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-2xl">{session.product_image ?? '🍽️'}</span>
                    <div>
                        <p className="font-semibold text-foreground">{session.product_name}</p>
                        <p className="text-xs text-muted-foreground">{session.oven_name}</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-xs text-muted-foreground">{t('temp.targetTemp')}</p>
                    <p className="font-semibold text-orange-500">{session.target_temp}°C</p>
                </div>
            </div>
            <CookingTimer startTime={session.start_time} cookTimeMinutes={session.cook_time_minutes} />
            <div className="flex gap-2 pt-1">
                <button onClick={() => onComplete(session.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-500 hover:bg-green-500/20 text-sm font-medium transition-colors">
                    <Square className="w-3.5 h-3.5" />{t('temp.confirmCooking')}
                </button>
                <button onClick={() => onCancel(session.id)}
                    className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground hover:text-foreground text-sm font-medium transition-colors">
                    {t('temp.cancelCooking')}
                </button>
            </div>
        </div>
    );
});

// ── Constants ──────────────────────────────────────────────────────────────────

const TABS = [
    { id: 'overview', icon: LayoutDashboard },
    { id: 'freezers', icon: Snowflake },
    { id: 'ovens',    icon: Flame },
    { id: 'cooking',  icon: ChefHat },
    { id: 'devices',  icon: Settings2 },
];

const POLL_MS     = 5000;
const LOG_POLL_MS = 30000;

// ── Main page ──────────────────────────────────────────────────────────────────

export function Temperature() {
    const { t }        = useTranslation();
    const { token }    = useAuth();
    const { addToast } = useToast();

    const [activeTab,       setActiveTab]       = useState('overview');
    const [devices,         setDevices]         = useState([]);
    const [products,        setProducts]        = useState([]);
    const [sessions,        setSessions]        = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [expandedIds,     setExpandedIds]     = useState(new Set());
    const [deviceLogs,      setDeviceLogs]      = useState({});
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedOvenId,  setSelectedOvenId]  = useState('');
    const [starting,        setStarting]        = useState(false);
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [editingDevice,   setEditingDevice]   = useState(null);

    // Stable token ref — avoids recreating fetch callbacks on token change
    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);

    const authHeader = useCallback(
        () => ({ Authorization: `Bearer ${tokenRef.current}` }),
        []
    );

    // ── Fetch helpers ──────────────────────────────────────────────────────────

    const fetchDevices = useCallback(async () => {
        try {
            const res = await fetch('/api/temperature/devices', { headers: authHeader() });
            if (res.ok) setDevices(await res.json());
        } catch (e) { console.error('fetchDevices', e); }
    }, [authHeader]);

    const fetchProducts = useCallback(async () => {
        try {
            const res = await fetch('/api/temperature/products', { headers: authHeader() });
            if (res.ok) setProducts(await res.json());
        } catch (e) { console.error('fetchProducts', e); }
    }, [authHeader]);

    const fetchSessions = useCallback(async () => {
        try {
            const res = await fetch('/api/temperature/sessions?status=active', { headers: authHeader() });
            if (res.ok) setSessions(await res.json());
        } catch (e) { console.error('fetchSessions', e); }
    }, [authHeader]);

    const fetchDeviceLogs = useCallback(async (deviceId) => {
        try {
            const res = await fetch(`/api/temperature/devices/${deviceId}/logs?limit=60`, { headers: authHeader() });
            if (res.ok) { const data = await res.json(); setDeviceLogs(prev => ({ ...prev, [deviceId]: data })); }
        } catch (e) { console.error('fetchDeviceLogs', e); }
    }, [authHeader]);

    // ── Polling ────────────────────────────────────────────────────────────────

    useEffect(() => {
        let mounted = true;
        Promise.all([fetchDevices(), fetchProducts(), fetchSessions()]).finally(() => { if (mounted) setLoading(false); });
        const id = setInterval(() => { fetchDevices(); fetchSessions(); }, POLL_MS);
        return () => { mounted = false; clearInterval(id); };
    }, [fetchDevices, fetchProducts, fetchSessions]);

    const expandedIdsRef = useRef(expandedIds);
    useEffect(() => { expandedIdsRef.current = expandedIds; }, [expandedIds]);

    useEffect(() => {
        if (expandedIds.size === 0) return;
        const id = setInterval(() => { expandedIdsRef.current.forEach(did => fetchDeviceLogs(did)); }, LOG_POLL_MS);
        return () => clearInterval(id);
    }, [expandedIds, fetchDeviceLogs]);

    // ── Handlers ───────────────────────────────────────────────────────────────

    const toggleExpand = useCallback((deviceId) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(deviceId) ? next.delete(deviceId) : next.add(deviceId);
            return next;
        });
    }, []);

    const startCooking = async () => {
        if (!selectedProduct || !selectedOvenId) return;
        const product = selectedProduct;
        const ovenId  = parseInt(selectedOvenId, 10);
        const tempId  = `opt-${Date.now()}`;
        setSelectedProduct(null);
        setSelectedOvenId('');
        setStarting(true);
        const optimisticSession = {
            id: tempId, product_id: product.id, product_name: product.name,
            product_image: product.image, oven_id: ovenId,
            oven_name: devices.find(d => d.id === ovenId)?.name ?? '',
            start_time: new Date().toISOString(), status: 'active',
            cook_time_minutes: product.cook_time_minutes, target_temp: product.target_temp,
            remaining_minutes: product.cook_time_minutes,
        };
        setSessions(prev => [...prev, optimisticSession]);
        setDevices(prev => prev.map(d => d.id === ovenId ? { ...d, target_temp: product.target_temp, has_active_session: true, status: 'ok' } : d));
        try {
            const res = await fetch('/api/temperature/sessions', {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_id: product.id, oven_id: ovenId }),
            });
            if (res.ok) {
                addToast({ type: 'success', message: t('temp.cookingStarted') });
            } else {
                setSessions(prev => prev.filter(s => s.id !== tempId));
                setDevices(prev => prev.map(d => d.id === ovenId ? { ...d, target_temp: 0, has_active_session: false, status: 'idle' } : d));
                addToast({ type: 'error', message: 'Erreur démarrage cuisson' });
            }
        } catch {
            setSessions(prev => prev.filter(s => s.id !== tempId));
        } finally {
            setStarting(false);
            fetchDevices();
            fetchSessions();
        }
    };

    const completeSession = useCallback(async (sessionId) => {
        const session = sessions.find(s => s.id === sessionId);
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (session?.oven_id) setDevices(prev => prev.map(d => d.id === session.oven_id ? { ...d, target_temp: 0, has_active_session: false, status: 'idle' } : d));
        try {
            const res = await fetch(`/api/temperature/sessions/${sessionId}/complete`, { method: 'PUT', headers: authHeader() });
            if (res.ok) addToast({ type: 'success', message: t('temp.cookingDone') });
            else fetchSessions();
        } catch { fetchSessions(); }
        finally { fetchDevices(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions, authHeader, addToast, t]);

    const cancelSession = useCallback(async (sessionId) => {
        const session = sessions.find(s => s.id === sessionId);
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (session?.oven_id) setDevices(prev => prev.map(d => d.id === session.oven_id ? { ...d, target_temp: 0, has_active_session: false, status: 'idle' } : d));
        try {
            const res = await fetch(`/api/temperature/sessions/${sessionId}/cancel`, { method: 'PUT', headers: authHeader() });
            if (res.ok) addToast({ type: 'success', message: t('temp.cookingStopped') });
            else fetchSessions();
        } catch { fetchSessions(); }
        finally { fetchDevices(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions, authHeader, addToast, t]);

    const deleteDevice = useCallback(async (deviceId) => {
        setDevices(prev => prev.filter(d => d.id !== deviceId));
        try {
            const res = await fetch(`/api/temperature/devices/${deviceId}`, { method: 'DELETE', headers: authHeader() });
            if (res.ok) addToast({ type: 'success', message: t('temp.deviceDeleted') });
            else fetchDevices();
        } catch { fetchDevices(); }
    }, [authHeader, addToast, t, fetchDevices]);

    const openAddDevice  = () => { setEditingDevice(null);  setShowDeviceModal(true); };
    const openEditDevice = (d) => { setEditingDevice(d);    setShowDeviceModal(true); };

    // ── Derived ────────────────────────────────────────────────────────────────

    const freezers     = devices.filter(d => d.type === 'freezer');
    const ovens        = devices.filter(d => d.type === 'oven');
    const statusCounts = devices.reduce((acc, d) => { acc[d.status] = (acc[d.status] ?? 0) + 1; return acc; }, {});

    if (loading) return (
        <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />Chargement…
        </div>
    );

    return (
        <div className="space-y-6 pb-10">

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <Thermometer className="w-7 h-7 text-primary" />{t('temp.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">{t('temp.subtitle')}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(statusCounts).map(([st, n]) => {
                        const cfg = STATUS[st] ?? STATUS.offline;
                        return (
                            <span key={st} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{n} {statusLabel(st, t)}
                            </span>
                        );
                    })}
                </div>
            </div>

            {/* Active sessions banner */}
            {sessions.length > 0 && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Flame className="w-5 h-5 text-orange-500" />
                        <span className="font-semibold text-foreground">{sessions.length} cuisson{sessions.length > 1 ? 's' : ''} en cours</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {sessions.map(s => <ActiveSessionCard key={s.id} session={s} onComplete={completeSession} onCancel={cancelSession} t={t} />)}
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit flex-wrap">
                {TABS.map(tab => {
                    const Icon   = tab.icon;
                    const active = activeTab === tab.id;
                    return (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                                ${active ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                            <Icon className="w-4 h-4" />{t(`temp.tab_${tab.id}`)}
                        </button>
                    );
                })}
            </div>

            {/* ── Overview ── */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {devices.length === 0 && <p className="col-span-full text-muted-foreground text-sm">{t('temp.noDevices')}</p>}
                    {devices.map(d => (
                        <DeviceCard key={d.id} device={d} logs={deviceLogs[d.id]}
                            expanded={expandedIds.has(d.id)} onExpand={toggleExpand} onLoadLogs={fetchDeviceLogs} t={t} />
                    ))}
                </div>
            )}

            {/* ── Freezers ── */}
            {activeTab === 'freezers' && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Snowflake className="w-4 h-4 text-blue-400" />
                        <span>{freezers.length} congélateur{freezers.length !== 1 ? 's' : ''}</span>
                    </div>
                    {freezers.length === 0 && <p className="text-muted-foreground text-sm">{t('temp.noDevices')}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {freezers.map(d => {
                            const cfg = STATUS[d.status] ?? STATUS.offline;
                            const exp = expandedIds.has(d.id);
                            return (
                                <div key={d.id} className={`bg-card border rounded-xl p-5 ${cfg.border}`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${cfg.bg}`}><Snowflake className="w-5 h-5 text-blue-400" /></div>
                                            <div>
                                                <p className="font-semibold text-foreground">{d.name}</p>
                                                {d.location && <p className="text-xs text-muted-foreground">{d.location}</p>}
                                                <SourceBadge sourceType={d.source_type} online={d.online} />
                                            </div>
                                        </div>
                                        <StatusBadge status={d.status} t={t} />
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 text-center py-4 border-y border-border mb-4">
                                        <div><p className="text-xs text-muted-foreground mb-1">{t('temp.currentTemp')}</p><TempValue value={d.current_temp} size="xl" /></div>
                                        <div><p className="text-xs text-muted-foreground mb-1">{t('temp.targetTemp')}</p><span className="text-2xl font-bold text-blue-400">{d.target_temp}°</span></div>
                                        <div><p className="text-xs text-muted-foreground mb-1">{t('temp.deviation')}</p><div className="flex justify-center mt-1"><DeviationBadge deviation={d.deviation} /></div></div>
                                    </div>
                                    <div className="flex gap-4 text-xs text-muted-foreground mb-4">
                                        <span>⚠ Seuil haut : +{d.alert_threshold_high}°C</span>
                                        <span>⚠ Seuil bas : -{d.alert_threshold_low}°C</span>
                                    </div>
                                    <button onClick={() => { if (!exp) fetchDeviceLogs(d.id); toggleExpand(d.id); }}
                                        className="w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1">
                                        {exp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}{t('temp.history')} (30 min)
                                    </button>
                                    {exp && <div className="mt-3 border-t border-border pt-3">
                                        <TempChart logs={deviceLogs[d.id]} targetTemp={d.target_temp} maxTemp={null} deviceType="freezer" />
                                    </div>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Ovens ── */}
            {activeTab === 'ovens' && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Flame className="w-4 h-4 text-orange-400" />
                        <span>{ovens.length} four{ovens.length !== 1 ? 's' : ''}</span>
                    </div>
                    {ovens.length === 0 && <p className="text-muted-foreground text-sm">{t('temp.noDevices')}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {ovens.map(d => {
                            const activeSession = sessions.find(s => s.oven_id === d.id);
                            const cfg = STATUS[d.status] ?? STATUS.offline;
                            const exp = expandedIds.has(d.id);
                            return (
                                <div key={d.id} className={`bg-card border rounded-xl p-5 ${cfg.border}`}>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${cfg.bg}`}><Flame className="w-5 h-5 text-orange-400" /></div>
                                            <div>
                                                <p className="font-semibold text-foreground">{d.name}</p>
                                                {d.location && <p className="text-xs text-muted-foreground">{d.location}</p>}
                                                <SourceBadge sourceType={d.source_type} online={d.online} />
                                            </div>
                                        </div>
                                        <StatusBadge status={d.status} t={t} />
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 text-center py-4 border-y border-border mb-4">
                                        <div><p className="text-xs text-muted-foreground mb-1">{t('temp.currentTemp')}</p><TempValue value={d.current_temp} size="xl" /></div>
                                        <div><p className="text-xs text-muted-foreground mb-1">{t('temp.targetTemp')}</p>
                                            <span className={`text-2xl font-bold ${activeSession ? 'text-orange-400' : 'text-muted-foreground'}`}>
                                                {activeSession ? `${d.target_temp}°` : '--'}
                                            </span></div>
                                        <div><p className="text-xs text-muted-foreground mb-1">Max</p><span className="text-2xl font-bold text-red-400">{d.max_temp ?? '--'}°</span></div>
                                    </div>
                                    {activeSession
                                        ? <ActiveSessionCard session={activeSession} onComplete={completeSession} onCancel={cancelSession} t={t} />
                                        : <p className="text-sm text-muted-foreground text-center py-2">{t('temp.noActiveSession')}</p>}
                                    <button onClick={() => { if (!exp) fetchDeviceLogs(d.id); toggleExpand(d.id); }}
                                        className="mt-3 w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1">
                                        {exp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}{t('temp.heatingCurve')}
                                    </button>
                                    {exp && <div className="mt-2 border-t border-border pt-3">
                                        <TempChart logs={deviceLogs[d.id]} targetTemp={activeSession ? d.target_temp : null} maxTemp={d.max_temp} deviceType="oven" />
                                    </div>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Cooking ── */}
            {activeTab === 'cooking' && (
                <div className="space-y-6">
                    {sessions.length > 0 && (
                        <div>
                            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Timer className="w-4 h-4 text-orange-400" />Cuissons en cours
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {sessions.map(s => <ActiveSessionCard key={s.id} session={s} onComplete={completeSession} onCancel={cancelSession} t={t} />)}
                            </div>
                        </div>
                    )}
                    <div>
                        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <ChefHat className="w-4 h-4 text-primary" />Sélectionner un produit
                        </h2>
                        {products.length === 0
                            ? <p className="text-muted-foreground text-sm">{t('temp.noProducts')}</p>
                            : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                {products.map(p => <ProductCard key={p.id} product={p} selected={selectedProduct?.id === p.id} onSelect={setSelectedProduct} />)}
                            </div>}
                    </div>
                    {selectedProduct && (
                        <div className="bg-card border border-primary/30 rounded-xl p-5 space-y-4">
                            <div className="flex items-center gap-4">
                                <span className="text-5xl">{selectedProduct.image}</span>
                                <div>
                                    <h3 className="text-xl font-bold text-foreground">{selectedProduct.name}</h3>
                                    <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1"><Thermometer className="w-3.5 h-3.5 text-orange-400" />Consigne : <strong className="text-foreground ml-1">{selectedProduct.target_temp}°C</strong></span>
                                        <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-red-400" />Max : <strong className="text-foreground ml-1">{selectedProduct.max_temp}°C</strong></span>
                                        <span className="flex items-center gap-1"><Timer className="w-3.5 h-3.5 text-primary" />Durée : <strong className="text-foreground ml-1">{selectedProduct.cook_time_minutes} min</strong></span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                                <div className="flex-1">
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">{t('temp.selectOven')}</label>
                                    <select value={selectedOvenId} onChange={e => setSelectedOvenId(e.target.value)}
                                        className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40">
                                        <option value="">-- Choisir un four --</option>
                                        {ovens.filter(o => !sessions.find(s => s.oven_id === o.id)).map(o => (
                                            <option key={o.id} value={o.id}>{o.name}{o.location ? ` (${o.location})` : ''}</option>
                                        ))}
                                    </select>
                                </div>
                                <button onClick={startCooking} disabled={!selectedOvenId || starting}
                                    className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                    <Play className="w-4 h-4" />{starting ? 'Démarrage…' : t('temp.startCooking')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Devices management ── */}
            {activeTab === 'devices' && (
                <DeviceManagementPanel
                    devices={devices}
                    onAdd={openAddDevice}
                    onEdit={openEditDevice}
                    onDelete={deleteDevice}
                    t={t}
                />
            )}

            {/* Device add/edit modal */}
            {showDeviceModal && (
                <DeviceModal
                    device={editingDevice}
                    onClose={() => setShowDeviceModal(false)}
                    onSaved={fetchDevices}
                    authHeader={authHeader}
                    addToast={addToast}
                    t={t}
                />
            )}
        </div>
    );
}
